import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Title from '../../../components/ui/newDesigns/Title';
import TotalValueCard from '../../../components/ui/newDesigns/TotalValueCard';
import PortfolioAccountAccordion from '../../../components/ui/newDesigns/PortfolioAccountAccordion';
import Loader from '../../../components/ui/loader';

import { getPortfolioAccounts } from '../../../redux/portfolioOverview/portfolioOverview.slice';
import { getBusinessDays } from '../../../redux/businessDay/businessDay.slice';
import { usePrivateLayout } from '../../../layout/PrivateLayoutContext';

const MyAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setBannerChildren } = usePrivateLayout();

  const { user } = useSelector((state) => state.auth);

  const { portfolioAccounts, loadingPortfolioAccounts, selectedPortfolioName, selectedAccountIds } =
    useSelector((state) => state.portfolioOverview);

  const { businessDays } = useSelector((state) => state.businessDay);

  // Fetch the latest South African business day.
  useEffect(() => {
    dispatch(
      getBusinessDays({
        countryCode: 'ZA',
        lag: -2
      })
    )
      .unwrap()
      .catch((error) => {
        console.error('MyAccount: Business Days error:', error);
      });
  }, [dispatch]);

  // Fetch portfolio accounts using the business-day date.
  useEffect(() => {
    if (!selectedAccountIds?.length) {
      navigate('/balances', { replace: true });
      return;
    }

    if (!businessDays?.date) {
      return;
    }

    const latestBalanceDate = dayjs(businessDays.date);

    if (!latestBalanceDate.isValid()) {
      console.error('MyAccount: Invalid business day:', businessDays.date);
      return;
    }

    const request = {
      accountIds: selectedAccountIds,
      date: latestBalanceDate.format('YYYY-MM-DD')
    };

    dispatch(getPortfolioAccounts(request))
      .unwrap()
      .catch((error) => {
        console.error('MyAccount: Portfolio Accounts error:', error);
      });
  }, [dispatch, navigate, selectedAccountIds, businessDays?.date]);

  const clientName = user?.personalClient?.name ?? '';
  const clientIdNr = user?.personalClient?.idNr ?? '';

  const totalValue = useMemo(() => {
    return (portfolioAccounts ?? []).reduce(
      (total, account) => total + Number(account?.accountNav ?? 0),
      0
    );
  }, [portfolioAccounts]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  };

  useEffect(() => {
    setBannerChildren(
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
        <Title
          title={selectedPortfolioName || 'Portfolio'}
          subtitle={clientName ? `${clientName} - ID: ${clientIdNr}` : `ID: ${clientIdNr}`}
        />

        <TotalValueCard
          total={loadingPortfolioAccounts ? 'R 0.00' : `R ${formatCurrency(totalValue)}`}
        />
      </Box>
    );

    return () => {
      setBannerChildren(null);
    };
  }, [
    setBannerChildren,
    selectedPortfolioName,
    clientName,
    clientIdNr,
    loadingPortfolioAccounts,
    totalValue
  ]);

  return (
    <>
      <Loader loading={loadingPortfolioAccounts} message="Loading portfolio accounts..." />

      <Box
        width="100%"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          pt: { xs: 7, md: 0 }
        }}>
        <PortfolioAccountAccordion accounts={portfolioAccounts} />
      </Box>
    </>
  );
};

export default MyAccount;
